import { useCallback, useEffect, useRef, useState } from "react";
import {
  addPoint,
  closeGame,
  resetTableScore,
  scoreboardHasScores,
  syncScoreboardTables,
  undoLastGame,
} from "../utils/matchScoreboard";

/**
 * Coach-owned match / challenge scoreboard keyed by table. Follows the current
 * seating and publishes every change to players via `liveModeSync`.
 */
export function useCoachMatchScoreboard({
  teId,
  seatedTables = [],
  defaultBestOf = 3,
  enabled = true,
  synced = null,
  publish = null,
}) {
  const [board, setBoard] = useState({ bestOf: defaultBestOf, tables: [] });
  const restoredTeRef = useRef(null);
  const dirtyRef = useRef(false);
  const publishRef = useRef(publish);
  publishRef.current = publish;

  useEffect(() => {
    setBoard({ bestOf: defaultBestOf, tables: [] });
    restoredTeRef.current = null;
    dirtyRef.current = false;
  }, [teId, defaultBestOf]);

  useEffect(() => {
    if (!enabled) return;
    setBoard((cur) => ({ ...cur, tables: syncScoreboardTables(cur.tables, seatedTables) }));
  }, [enabled, seatedTables]);

  // Restore scores already published for this exercise (coach reloaded).
  useEffect(() => {
    if (!enabled || !synced?.state || restoredTeRef.current === teId) return;
    restoredTeRef.current = teId;
    setBoard((cur) => {
      if (scoreboardHasScores(cur.tables)) return cur;
      const remote = synced.state;
      return {
        bestOf: remote.bestOf || cur.bestOf,
        tables: syncScoreboardTables(remote.tables || [], seatedTables),
      };
    });
  }, [enabled, synced, teId, seatedTables]);

  useEffect(() => {
    if (!enabled || !dirtyRef.current) return;
    publishRef.current?.("match", board);
  }, [board, enabled]);

  const updateTable = useCallback((tableNumber, fn) => {
    dirtyRef.current = true;
    setBoard((cur) => ({
      ...cur,
      tables: cur.tables.map((t) =>
        t.tableNumber === tableNumber ? fn(t, cur.bestOf) : t
      ),
    }));
  }, []);

  return {
    board,
    setBestOf: (bestOf) => {
      dirtyRef.current = true;
      setBoard((cur) => ({ ...cur, bestOf }));
    },
    point: (tableNumber, side, delta) =>
      updateTable(tableNumber, (t, bestOf) => addPoint(t, side, delta, bestOf)),
    endGame: (tableNumber) =>
      updateTable(tableNumber, (t, bestOf) => closeGame(t, bestOf)),
    undoGame: (tableNumber) => updateTable(tableNumber, (t) => undoLastGame(t)),
    resetTable: (tableNumber) => updateTable(tableNumber, (t) => resetTableScore(t)),
  };
}

export default useCoachMatchScoreboard;
